import { config, getApiOrigin, getCheckoutOrigin } from './config';
import { assertValidElementsAppearance, cloneElementsAppearance } from './elements/appearance';
import { ElementsGroupImpl } from './elements/group';
import { createSubmitError } from './elements/payment-method-submission';
import { containsAsciiControlCharacter } from './elements/protocol-validation';
import { normalizeBillingDetails } from './elements/billing-details';
import { PaymentConfirmationCoordinator } from './elements/payment-confirmation';
import { announceDeveloperAssistantCore } from './elements/developer-assistant-registration';

announceDeveloperAssistantCore();

const MAX_PAYMENT_METHOD_BILLING_CACHE_SIZE = 16;
import type {
  PaypercutElements,
  PaypercutElementsOptions,
  PaypercutCreatePaymentMethodOptions,
  PaypercutConfirmPaymentOptions,
  PaypercutConfirmPaymentResult,
  PaypercutBillingDetails,
  PaypercutSetupFutureUsage,
  DeferredPaymentReference,
  PaypercutInstance,
  PaypercutOptions,
  PaypercutStatic,
} from './types/elements';

const MAX_PUBLISHABLE_KEY_LENGTH = 512;
const MAX_CREATE_PAYMENT_METHOD_OPTION_KEYS = 2;
const CURRENCY_PATTERN = /^[A-Za-z]{3}$/;
const ELEMENTS_MODES = new Set(['payment', 'setup', 'subscription']);
const ELEMENTS_OPTION_KEYS = new Set([
  'mode',
  'amount',
  'currency',
  'setupFutureUsage',
  'locale',
  'appearanceUpdates',
  'appearance',
]);

class PaypercutImpl implements PaypercutInstance {
  private readonly configurationIdentity: string;
  private readonly groups = new WeakSet<ElementsGroupImpl>();
  private readonly paymentMethodBilling = new Map<
    string,
    Pick<PaypercutBillingDetails, 'name' | 'email'>
  >();
  private paymentConfirmation: PaymentConfirmationCoordinator | null = null;

  constructor(private readonly options: PaypercutOptions) {
    this.configurationIdentity = JSON.stringify([
      options.publishableKey,
      options.account ?? null,
      getApiOrigin(options.apiUrl),
      getCheckoutOrigin(options.hostedCheckoutUrl),
    ]);
  }

  elements(options: PaypercutElementsOptions): PaypercutElements {
    const group = new ElementsGroupImpl(
      this.options,
      validateElementsOptions(options),
      this.configurationIdentity,
    );
    this.groups.add(group);
    return group;
  }

  createPaymentMethod(
    options: PaypercutCreatePaymentMethodOptions,
  ): Promise<DeferredPaymentReference> {
    const keys =
      options && typeof options === 'object' && !Array.isArray(options) ? Object.keys(options) : [];
    if (
      !options ||
      typeof options !== 'object' ||
      Array.isArray(options) ||
      keys.length < 1 ||
      keys.length > MAX_CREATE_PAYMENT_METHOD_OPTION_KEYS ||
      keys.some((key) => key !== 'elements' && key !== 'params') ||
      !('elements' in options) ||
      !(options.elements instanceof ElementsGroupImpl) ||
      !this.groups.has(options.elements)
    ) {
      return Promise.reject(
        createSubmitError(
          'elements_component_configuration_invalid',
          'createPaymentMethod requires an Elements group created by this Paypercut client and optional params.billing_details.',
          false,
        ),
      );
    }
    let billingDetails;
    try {
      const params = options.params;
      if (params !== undefined) {
        if (
          !params ||
          typeof params !== 'object' ||
          Array.isArray(params) ||
          Object.keys(params).length !== 1 ||
          !Object.prototype.hasOwnProperty.call(params, 'billing_details') ||
          params.billing_details === undefined
        ) {
          throw new Error(
            '[Paypercut Elements] createPaymentMethod params must contain only billing_details.',
          );
        }
      }
      billingDetails = normalizeBillingDetails(params?.billing_details);
    } catch (error) {
      return Promise.reject(
        createSubmitError(
          'elements_component_configuration_invalid',
          error instanceof Error ? error.message : 'Billing details are invalid.',
          false,
        ),
      );
    }
    return options.elements.createPaymentMethod(billingDetails).then((reference) => {
      const identity = billingDetails
        ? {
            ...(billingDetails.name ? { name: billingDetails.name } : {}),
            ...(billingDetails.email ? { email: billingDetails.email } : {}),
          }
        : undefined;
      if (identity && Object.keys(identity).length > 0) {
        this.paymentMethodBilling.set(reference.id, identity);
        if (this.paymentMethodBilling.size > MAX_PAYMENT_METHOD_BILLING_CACHE_SIZE) {
          const oldest = this.paymentMethodBilling.keys().next().value;
          if (typeof oldest === 'string') this.paymentMethodBilling.delete(oldest);
        }
      }
      return reference;
    });
  }

  confirmPayment(options: PaypercutConfirmPaymentOptions): Promise<PaypercutConfirmPaymentResult> {
    return this.confirmPaymentInput(options);
  }

  handleNextAction(
    options: PaypercutConfirmPaymentOptions,
  ): Promise<PaypercutConfirmPaymentResult> {
    return this.confirmPaymentInput(options);
  }

  private confirmPaymentInput(
    options: PaypercutConfirmPaymentOptions,
  ): Promise<PaypercutConfirmPaymentResult> {
    if (
      !options ||
      typeof options !== 'object' ||
      Array.isArray(options) ||
      Object.keys(options).length !== 1 ||
      !Object.prototype.hasOwnProperty.call(options, 'clientSecret')
    ) {
      return Promise.reject(
        createSubmitError(
          'elements_client_secret_invalid',
          'confirmPayment requires only a Checkout Session clientSecret.',
          false,
        ),
      );
    }
    this.paymentConfirmation ??= new PaymentConfirmationCoordinator(
      this.options.publishableKey,
      {
        ...(this.options.account !== undefined ? { account: this.options.account } : {}),
        ...(this.options.apiUrl ? { apiUrl: this.options.apiUrl } : {}),
        ...(this.options.hostedCheckoutUrl
          ? { hostedCheckoutUrl: this.options.hostedCheckoutUrl }
          : {}),
      },
      (paymentMethodId) => this.paymentMethodBilling.get(paymentMethodId),
    );
    return this.paymentConfirmation.confirm(options.clientSecret);
  }
}

function isSetupFutureUsage(value: unknown): value is PaypercutSetupFutureUsage | undefined {
  return value === undefined || value === 'on_session' || value === 'off_session';
}

const Paypercut: PaypercutStatic = function (
  optionsOrKey: PaypercutOptions | string,
  options?: Omit<PaypercutOptions, 'publishableKey'>,
): PaypercutInstance {
  if (typeof optionsOrKey === 'string') {
    if (
      options !== undefined &&
      (!options || typeof options !== 'object' || Array.isArray(options))
    ) {
      throw new Error('[Paypercut Elements] Client options must be an object.');
    }
    return new PaypercutImpl(validateClientOptions({ ...options, publishableKey: optionsOrKey }));
  }
  if (options !== undefined) {
    throw new Error('[Paypercut Elements] Pass client options in one initializer object.');
  }
  return new PaypercutImpl(validateClientOptions(optionsOrKey));
} as PaypercutStatic;

Paypercut.version = config.version;

function validateClientOptions(options: PaypercutOptions): PaypercutOptions {
  if (!options || typeof options !== 'object' || Array.isArray(options)) {
    throw new Error('[Paypercut Elements] Client options are required.');
  }

  const publishableKey = options.publishableKey;
  if (
    typeof publishableKey !== 'string' ||
    publishableKey.length === 0 ||
    publishableKey.length > MAX_PUBLISHABLE_KEY_LENGTH ||
    publishableKey !== publishableKey.trim() ||
    containsAsciiControlCharacter(publishableKey)
  ) {
    throw new Error('[Paypercut Elements] A valid publishable key is required.');
  }
  if (options.developerAssistant !== undefined && typeof options.developerAssistant !== 'boolean') {
    throw new Error('[Paypercut Elements] Developer assistant access must be a boolean.');
  }
  return { ...options, publishableKey };
}

function validateElementsOptions(options: PaypercutElementsOptions): PaypercutElementsOptions {
  if (!options || typeof options !== 'object' || Array.isArray(options)) {
    throw new Error('[Paypercut Elements] Elements options are required.');
  }
  if (Object.keys(options).some((key) => !ELEMENTS_OPTION_KEYS.has(key))) {
    throw new Error('[Paypercut Elements] Elements options contain an unsupported property.');
  }
  if (!ELEMENTS_MODES.has(options.mode)) {
    throw new Error('[Paypercut Elements] Mode must be payment, setup, or subscription.');
  }
  if (!Number.isSafeInteger(options.amount) || options.amount < 0) {
    throw new Error('[Paypercut Elements] Amount must be a non-negative integer in minor units.');
  }
  if (typeof options.currency !== 'string' || !CURRENCY_PATTERN.test(options.currency)) {
    throw new Error('[Paypercut Elements] Currency must be a three-letter ISO 4217 code.');
  }
  if (!isSetupFutureUsage(options.setupFutureUsage)) {
    throw new Error('[Paypercut Elements] setupFutureUsage must be on_session or off_session.');
  }
  if (
    options.appearanceUpdates !== undefined &&
    options.appearanceUpdates !== 'locked' &&
    options.appearanceUpdates !== 'remount'
  ) {
    throw new Error('[Paypercut Elements] appearanceUpdates must be locked or remount.');
  }
  if (
    options.mode !== 'payment' &&
    options.setupFutureUsage !== undefined &&
    options.setupFutureUsage !== 'off_session'
  ) {
    throw new Error(
      `[Paypercut Elements] ${options.mode} mode prepares cards for off-session use and cannot use setupFutureUsage on_session.`,
    );
  }
  if (options.appearance) {
    assertValidElementsAppearance(options.appearance);
  }

  return {
    ...options,
    currency: options.currency.toUpperCase(),
    ...(options.mode === 'payment'
      ? options.setupFutureUsage
        ? { setupFutureUsage: options.setupFutureUsage }
        : {}
      : { setupFutureUsage: 'off_session' as const }),
    appearanceUpdates: options.appearanceUpdates ?? 'locked',
    ...(options.appearance ? { appearance: cloneElementsAppearance(options.appearance) } : {}),
  };
}

export { Paypercut };
export default Paypercut;
